import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Calendar,
  AlertTriangle,
  Loader2,
  ClipboardList,
  GraduationCap,
  Wallet,
  CheckCircle2,
} from 'lucide-react';
import { fetchGroups, fetchAdvanceLoansTableView } from '../Users/api';

// ---------------------------------------------------------------------
// Wired to the real /advance-loans/table_view/ endpoint. The backend
// handles the heavy filter — the disbursement date range — returning
// every advance loan (all types) in that range so the 3 cards can each
// show their own count/total from one dataset. Group/officer/search
// filtering stays client-side on that (already date-filtered) result
// set, same pattern used elsewhere in this app.
//
// Advance loans have no approval workflow ("they don't have a process")
// — a loan is created already disbursed — so there's no status filter:
// the STATUS column always reads "Disbursed" plus its disbursement date,
// and the 3 cards filter by type (all / regular / school fees) instead.
// ---------------------------------------------------------------------

const TYPE_LABELS = {
  sf: 'SF',
  regular: 'Regular',
};

// The 3 cards ARE the type filter for this page — no separate dropdown.
const TYPE_CARDS = [
  { key: 'all', label: 'All Advance Loans', icon: ClipboardList },
  { key: 'regular', label: 'Regular Advance Loans', icon: Wallet },
  { key: 'sf', label: 'School Fees Advances', icon: GraduationCap },
];

const matchesType = (type, cardKey) => {
  if (cardKey === 'all') return true;
  return type === cardKey;
};

const CARD_ACCENT = {
  all: {
    icon: (isDark) => (isDark ? 'text-gray-300' : 'text-gray-600'),
    active: (isDark) => (isDark ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50'),
  },
  regular: {
    icon: (isDark) => (isDark ? 'text-teal-400' : 'text-teal-600'),
    active: (isDark) => (isDark ? 'border-teal-500 bg-teal-900/20' : 'border-teal-500 bg-teal-50'),
  },
  sf: {
    icon: (isDark) => (isDark ? 'text-indigo-400' : 'text-indigo-600'),
    active: (isDark) => (isDark ? 'border-indigo-500 bg-indigo-900/20' : 'border-indigo-500 bg-indigo-50'),
  },
};

// Default date range: past 60 days, same as Long-term Loans.
const getPast60DaysRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 60);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
};

const AdvanceLoans = ({ isDark = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [advanceLoans, setAdvanceLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all'); // driven only by the cards
  const [groupFilter, setGroupFilter] = useState('all');
  const [officerFilter, setOfficerFilter] = useState('all');
  const [groups, setGroups] = useState([]);
  const [dateRange, setDateRange] = useState(getPast60DaysRange());
  const [activeDatePreset, setActiveDatePreset] = useState('past60'); // 'past60' | 'month' | 'custom' | 'all'
  const [selectedMonth, setSelectedMonth] = useState('');
  const [monthMenuAnchor, setMonthMenuAnchor] = useState(null);
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState({ startDate: '', endDate: '' });

  useEffect(() => {
    loadGroups();
    loadAdvanceLoans();
  }, [dateRange]);

  const loadGroups = async () => {
    try {
      const groupsData = await fetchGroups();
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  // Fetches advance loans of ALL types within the disbursement date range —
  // the backend handles that date filtering. Type is filtered client-side
  // (via the 3 cards) so each card can show its own count/total from the
  // one dataset.
  const loadAdvanceLoans = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAdvanceLoansTableView(dateRange.startDate, dateRange.endDate);
      setAdvanceLoans(response?.results || []);
    } catch (err) {
      console.error('Error loading advance loans:', err);
      setError('Failed to load advance loans');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomDateApply = () => {
    setDateRange(tempDateRange);
    setSelectedMonth('');
    setActiveDatePreset('custom');
    setCustomDateDialogOpen(false);
  };

  const handleClearDateFilter = () => {
    setDateRange({ startDate: '', endDate: '' });
    setSelectedMonth('');
    setActiveDatePreset('all');
  };

  const handlePast60Days = () => {
    setDateRange(getPast60DaysRange());
    setSelectedMonth('');
    setActiveDatePreset('past60');
  };

  // Search + group + officer + disbursement-date range — everything
  // except type, so all 3 cards can be computed from one filtered set.
  const baseFiltered = useMemo(() => {
    let filtered = [...advanceLoans];

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(loan =>
        loan.member?.name?.toLowerCase().includes(query) ||
        loan.group?.name?.toLowerCase().includes(query) ||
        loan.id?.toString().includes(query)
      );
    }

    if (groupFilter !== 'all') {
      filtered = filtered.filter(loan => loan.group?.id?.toString() === groupFilter);
    }

    if (officerFilter !== 'all') {
      filtered = filtered.filter(loan => loan.officerName === officerFilter);
    }

    if (dateRange.startDate || dateRange.endDate) {
      filtered = filtered.filter(loan => {
        if (!loan.disbursementDate) return false;
        const date = new Date(loan.disbursementDate);
        const start = dateRange.startDate ? new Date(dateRange.startDate) : null;
        const end   = dateRange.endDate   ? new Date(dateRange.endDate)   : null;
        if (start && end) return date >= start && date <= end;
        if (start) return date >= start;
        if (end)   return date <= end;
        return true;
      });
    }

    return filtered;
  }, [advanceLoans, searchQuery, groupFilter, officerFilter, dateRange]);

  const cardStats = useMemo(() => {
    return TYPE_CARDS.map(card => {
      const matching = baseFiltered.filter(loan => matchesType(loan.type, card.key));
      const total = matching.reduce((sum, loan) => sum + parseFloat(loan.principal || 0), 0);
      return { ...card, count: matching.length, total };
    });
  }, [baseFiltered]);

  const filteredLoans = useMemo(
    () => baseFiltered.filter(loan => matchesType(loan.type, typeFilter)),
    [baseFiltered, typeFilter]
  );

  const officerOptions = useMemo(() => {
    const set = new Set();
    advanceLoans.forEach(loan => { if (loan.officerName) set.add(loan.officerName); });
    return Array.from(set).sort();
  }, [advanceLoans]);

  const formatCurrency = (amount) => `KSh ${parseFloat(amount || 0).toLocaleString()}`;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const totals = useMemo(() => {
    return filteredLoans.reduce((acc, loan) => {
      acc.principal += parseFloat(loan.principal || 0);
      acc.interest += parseFloat(loan.interest || 0);
      acc.totalRepayment += parseFloat(loan.totalRepayment || 0);
      return acc;
    }, { principal: 0, interest: 0, totalRepayment: 0 });
  }, [filteredLoans]);

  const getTypeBadgeColor = (type) => {
    if (type === 'sf') return isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-800';
    return isDark ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-100 text-teal-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Loading advance loans...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
        <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
        <button
          onClick={loadAdvanceLoans}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="pb-32">
      {/* Type cards - "all", "regular", "sf". These ARE the type filter;
          there's no separate Type dropdown in the filter row below. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {cardStats.map((card) => {
          const Icon = card.icon;
          const isActive = typeFilter === card.key;
          const accent = CARD_ACCENT[card.key];
          return (
            <button
              key={card.key}
              onClick={() => setTypeFilter(card.key)}
              className={`text-left rounded-lg border p-4 transition-colors ${
                isActive
                  ? accent.active(isDark)
                  : isDark
                    ? 'border-gray-700 bg-[#2b323c] hover:border-gray-600'
                    : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${accent.icon(isDark)}`} />
                <span className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {card.label}
                </span>
              </div>
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {card.count}
              </div>
              <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {formatCurrency(card.total)}
              </div>
            </button>
          );
        })}
      </div>

      {/* Main panel: title, filters, table and footer count all in one card. */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Title */}
        <div className={`px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Advance Loans
          </h2>
          <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {advanceLoans.length} advance loan{advanceLoans.length === 1 ? '' : 's'} disbursed in the selected date range.
          </p>
        </div>

        {/* Filters - no Type filter here, the cards above cover it */}
        <div className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="relative flex-1 min-w-[220px]">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search advance loans..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Group Filter */}
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Groups</option>
            {groups.map(group => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>

          {/* Officer Filter */}
          <select
            value={officerFilter}
            onChange={(e) => setOfficerFilter(e.target.value)}
            className={`px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="all">All Officers</option>
            {officerOptions.map(officer => (
              <option key={officer} value={officer}>
                {officer}
              </option>
            ))}
          </select>

          {/* Past 60 Days - default range */}
          <button
            onClick={handlePast60Days}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
              activeDatePreset === 'past60'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <Calendar className="w-4 h-4" />
            Past 60 Days
          </button>

          {/* Month Selector Button - filters by disbursement date */}
          <button
            onClick={(e) => setMonthMenuAnchor(e.currentTarget)}
            className={`px-4 py-2 border rounded-lg transition-colors text-left flex items-center justify-between ${
              activeDatePreset === 'month'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <span>
              {selectedMonth !== '' ? (() => {
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const [year, monthIndex] = selectedMonth.split('-');
                return `${monthNames[parseInt(monthIndex)]} ${year}`;
              })() : 'Select Month'}
            </span>
            <Calendar className="w-4 h-4 ml-2" />
          </button>

          {/* Custom Date Button */}
          <button
            onClick={() => {
              setTempDateRange(dateRange);
              setCustomDateDialogOpen(true);
            }}
            className={`px-4 py-2 border rounded-lg transition-colors flex items-center justify-center gap-2 ${
              activeDatePreset === 'custom'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                : isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <Calendar className="w-4 h-4" />
            Custom
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  TYPE
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  MEMBER
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  PRINCIPAL
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  INTEREST
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  TOTAL REPAYMENT
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  STATUS
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  OFFICER
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {filteredLoans.map((loan) => (
                <tr key={loan.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${getTypeBadgeColor(loan.type)}`}>
                      {TYPE_LABELS[loan.type] || loan.type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {loan.member?.name || 'N/A'}
                    </div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {loan.group?.name || 'No Group'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-blue-600">
                    {formatCurrency(loan.principal)}
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-orange-600">
                    {formatCurrency(loan.interest)}
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-green-600">
                    {formatCurrency(loan.totalRepayment)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                      isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'
                    }`}>
                      <CheckCircle2 className="w-3 h-3" />
                      Disbursed
                    </span>
                    <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {formatDate(loan.disbursementDate)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                      {loan.officerName || 'System'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className={`border-t-2 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
              <tr>
                <td className={`px-6 py-4 font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} colSpan="2">
                  TOTAL — {filteredLoans.length} advance loans
                </td>
                <td className="px-6 py-4 text-right font-bold text-blue-600">
                  {formatCurrency(totals.principal)}
                </td>
                <td className="px-6 py-4 text-right font-bold text-orange-600">
                  {formatCurrency(totals.interest)}
                </td>
                <td className="px-6 py-4 text-right font-bold text-green-600">
                  {formatCurrency(totals.totalRepayment)}
                </td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {filteredLoans.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className="text-lg">No advance loans found matching your filters.</p>
          </div>
        )}

        {/* Footer count */}
        <div className={`px-5 py-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
          Showing {filteredLoans.length} of {advanceLoans.length} advance loans
        </div>
      </div>

      {/* Month Selection Menu */}
      {monthMenuAnchor && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
          <div className="absolute inset-0 bg-black/20" onClick={() => setMonthMenuAnchor(null)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl border min-w-[200px] max-h-[500px] overflow-y-auto ${
            isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {(() => {
              const currentDate = new Date();
              const currentYear = currentDate.getFullYear();
              const currentMonth = currentDate.getMonth();
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

              const months = [];
              for (let i = 0; i < 12; i++) {
                const date = new Date(currentYear, currentMonth - i, 1);
                const monthIndex = date.getMonth();
                const year = date.getFullYear();
                months.push({
                  label: `${monthNames[monthIndex]} ${year}`,
                  monthIndex: monthIndex,
                  year: year,
                  value: `${year}-${monthIndex}`,
                  isCurrent: i === 0
                });
              }

              return months.map((month) => (
                <button
                  key={month.value}
                  onClick={() => {
                    const startDate = new Date(month.year, month.monthIndex, 1);
                    const endDate = new Date(month.year, month.monthIndex + 1, 0);
                    setDateRange({
                      startDate: startDate.toISOString().split('T')[0],
                      endDate: endDate.toISOString().split('T')[0]
                    });
                    setSelectedMonth(month.value);
                    setActiveDatePreset('month');
                    setMonthMenuAnchor(null);
                  }}
                  className={`w-full px-4 py-2 text-left transition-colors flex items-center justify-between ${
                    selectedMonth === month.value
                      ? isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'
                      : isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <span>{month.label}</span>
                  {month.isCurrent && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'
                    }`}>
                      Current
                    </span>
                  )}
                </button>
              ));
            })()}
            <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={handleClearDateFilter}
                className={`w-full px-4 py-2 text-left transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-red-400' : 'hover:bg-gray-50 text-red-600'
                }`}
              >
                Clear Filter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Dialog */}
      {customDateDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCustomDateDialogOpen(false)}></div>
          <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full ${
            isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white'
          }`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Custom Date Range
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.startDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, startDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? 'bg-[#1e252d] border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  End Date
                </label>
                <input
                  type="date"
                  value={tempDateRange.endDate}
                  onChange={(e) => setTempDateRange({ ...tempDateRange, endDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${
                    isDark
                      ? 'bg-[#1e252d] border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
            </div>
            <div className={`px-6 py-4 border-t flex justify-end gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setCustomDateDialogOpen(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDark
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleClearDateFilter}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={handleCustomDateApply}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvanceLoans;